import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, CheckCircle, Clock, AlertCircle, XCircle } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [message, setMessage] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTickets();
    fetchStats();
    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      if (selectedTicket) {
        fetchTicketDetails(selectedTicket.id);
      }
      fetchTickets();
    }, 10000);
    return () => clearInterval(interval);
  }, [filterStatus]);

  const fetchTickets = async () => {
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const { data } = await api.get(`/support/admin/tickets${params}`);
      setTickets(data.data);
    } catch (error) {
      console.error('Error fetching tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await api.get('/support/admin/stats');
      setStats(data.data);
    } catch (error) {
      console.error('Error fetching stats');
    }
  };

  const fetchTicketDetails = async (id) => {
    try {
      const ticket = tickets.find(t => t.id === id);
      if (ticket) {
        setSelectedTicket(ticket);
      }
    } catch (error) {
      console.error('Error fetching ticket');
    }
  };

  const handleSendReply = async () => {
    if (!message.trim() || !selectedTicket) return;

    try {
      const { data } = await api.post(`/support/admin/tickets/${selectedTicket.id}/reply`, {
        message: message.trim()
      });
      
      setSelectedTicket(data.data);
      setMessage('');
      toast.success('Reply sent');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const handleUpdateStatus = async (ticketId, status) => {
    try {
      await api.put(`/support/admin/tickets/${ticketId}/status`, { status });
      toast.success('Status updated');
      fetchTickets();
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, status }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdatePriority = async (ticketId, priority) => {
    try {
      await api.put(`/support/admin/tickets/${ticketId}/status`, { priority });
      toast.success('Priority updated');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-500/20 text-blue-500',
      in_progress: 'bg-yellow-500/20 text-yellow-500',
      resolved: 'bg-green-500/20 text-green-500',
      closed: 'bg-gray-500/20 text-gray-500'
    };
    return colors[status] || colors.open;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-green-500/20 text-green-500',
      medium: 'bg-yellow-500/20 text-yellow-500',
      high: 'bg-orange-500/20 text-orange-500',
      urgent: 'bg-red-500/20 text-red-500'
    };
    return colors[priority] || colors.medium;
  };

  if (loading) {
    return <div className="text-center py-20">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold mb-2">Support Tickets</h1>
          <p className="text-gray-400">Manage user support requests</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="gradient-card border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Tickets</p>
              <p className="text-2xl font-bold">{stats.totalTickets || 0}</p>
            </div>
            <MessageSquare className="text-primary" size={32} />
          </div>
        </div>

        <div className="gradient-card border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Open</p>
              <p className="text-2xl font-bold text-blue-500">{stats.openTickets || 0}</p>
            </div>
            <AlertCircle className="text-blue-500" size={32} />
          </div>
        </div>

        <div className="gradient-card border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">In Progress</p>
              <p className="text-2xl font-bold text-yellow-500">{stats.inProgressTickets || 0}</p>
            </div>
            <Clock className="text-yellow-500" size={32} />
          </div>
        </div>

        <div className="gradient-card border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Resolved</p>
              <p className="text-2xl font-bold text-green-500">{stats.resolvedTickets || 0}</p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilterStatus('all')}
          className={`px-4 py-2 rounded-lg ${filterStatus === 'all' ? 'bg-primary text-white' : 'bg-gray-800 text-gray-400'}`}
        >
          All
        </button>
        <button
          onClick={() => setFilterStatus('open')}
          className={`px-4 py-2 rounded-lg ${filterStatus === 'open' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'}`}
        >
          Open
        </button>
        <button
          onClick={() => setFilterStatus('in_progress')}
          className={`px-4 py-2 rounded-lg ${filterStatus === 'in_progress' ? 'bg-yellow-600 text-white' : 'bg-gray-800 text-gray-400'}`}
        >
          In Progress
        </button>
        <button
          onClick={() => setFilterStatus('resolved')}
          className={`px-4 py-2 rounded-lg ${filterStatus === 'resolved' ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-400'}`}
        >
          Resolved
        </button>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tickets List */}
        <div className="lg:col-span-1 gradient-card border border-gray-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h2 className="font-semibold">Tickets ({tickets.length})</h2>
          </div>
          <div className="overflow-y-auto max-h-[600px]">
            {tickets.map(ticket => (
              <div
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className={`p-4 border-b border-gray-800 cursor-pointer hover:bg-dark/50 transition ${
                  selectedTicket?.id === ticket.id ? 'bg-dark' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm mb-1">{ticket.subject}</h3>
                    <p className="text-xs text-gray-400">
                      by {ticket.user?.username} • {new Date(ticket.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(ticket.status)}`}>
                    {ticket.status}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(ticket.priority)}`}>
                    {ticket.priority}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {ticket.messages.length} messages
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Chat View */}
        <div className="lg:col-span-2 gradient-card border border-gray-800 rounded-xl overflow-hidden flex flex-col">
          {selectedTicket ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-gray-800">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-lg font-bold mb-1">{selectedTicket.subject}</h2>
                    <p className="text-sm text-gray-400">
                      {selectedTicket.user?.username} • {selectedTicket.category}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={selectedTicket.status}
                      onChange={(e) => handleUpdateStatus(selectedTicket.id, e.target.value)}
                      className="px-3 py-1 bg-dark border border-gray-700 rounded-lg text-sm"
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                    <select
                      value={selectedTicket.priority}
                      onChange={(e) => handleUpdatePriority(selectedTicket.id, e.target.value)}
                      className="px-3 py-1 bg-dark border border-gray-700 rounded-lg text-sm"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[400px]">
                {selectedTicket.messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] p-3 rounded-lg ${
                        msg.sender === 'admin'
                          ? 'bg-primary text-white'
                          : 'bg-dark-card border border-gray-700'
                      }`}
                    >
                      <p className="text-sm">{msg.message}</p>
                      <p className={`text-xs mt-1 ${msg.sender === 'admin' ? 'text-white/60' : 'text-gray-500'}`}>
                        {new Date(msg.createdAt).toLocaleString()} • {msg.sender}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Input */}
              <div className="p-4 border-t border-gray-800">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendReply()}
                    placeholder="Type your reply..."
                    className="flex-1 px-4 py-2 bg-dark border border-gray-700 rounded-lg focus:outline-none focus:border-primary text-white"
                  />
                  <button
                    onClick={handleSendReply}
                    disabled={!message.trim()}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Send size={16} />
                    Send
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              Select a ticket to view conversation
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Support;
